import emptyPlaceholderImage from '@/assets/images/empty-placeholder.png'
import placeholderImage from '@/assets/images/placeholder.png'
import { MenuItem } from '@/components/navigation/menu'
import { PortalSettings, PortalType } from '@/modules/portalSettings/types'
import useBranding from '@/modules/portalSettings/useBranding'
import { GuestUser } from '@/modules/user/types'
import router from '@/router'
import useRequestManager from '@/store/useRequestManager'
import {
  Attachment,
  ContactUser,
  ContentType,
  Money,
  OrderLimitUser,
  QuoteActivityUser,
  ShortUser,
  SimpleStock,
  User
} from '@/types/index'
import { Availability } from '@/types/types.gen'
import { useTitle as vueUseTitle } from '@vueuse/core'
import { AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'
import getSymbolFromCurrency from 'currency-symbol-map'
import { saveAs } from 'file-saver'
import { ComponentPublicInstance, ComputedRef, Ref, computed, reactive, toRef } from 'vue'
import { useI18n } from 'vue-i18n'

const getCurrencySymbol = (currency: string) => {
  return getSymbolFromCurrency(currency)
}

const fitCropImage = (url: string, width: number, height: number, separator = '?'): string => {
  return url + `${separator}fitcrop=${width}x${height}dBOTH`
}

const fitImage = (url: string, width: number, height: number, separator = '?'): string => {
  return url + `${separator}fit=${width}x${height}`
}

const getSrcset = (url: string, width: number, height: number, separator = '?'): string => {
  return `${url}${separator}fit=${width}x${height} 1x, ${url}${separator}fit=${width * 2}x${
    height * 2
  } 2x`
}

const getFullUrl = (url: string | undefined | null): string | undefined | null => {
  if (url && !url.startsWith('https://') && !url.startsWith('http://')) {
    return 'https://' + url
  } else {
    return url
  }
}

const disableSort = <T>(items: T[]): T[] => {
  return items
}

const stringCompare = (a?: string, b?: string): number => {
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

const numberCompare = (a?: number, b?: number): number => {
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }
  return a - b
}

const moneyCompare = (a?: Money, b?: Money): number => {
  if (!a) {
    return 1
  } else if (!b) {
    return -1
  }

  return a.value - b.value
}

const availabilityOrder = [
  Availability.ON_STOCK,
  Availability.AVAILABLE,
  Availability.ORDERED,
  Availability.WAREHOUSE,
  Availability.ON_ORDER,
  Availability.NOT_AVAILABLE,
  Availability.UNKNOWN
]

const statusCompare = (a?: SimpleStock, b?: SimpleStock): number => {
  if (!a) return 1
  if (!b) return -1

  const av = availabilityOrder.indexOf(a.status) ?? 0
  const bv = availabilityOrder.indexOf(b.status) ?? 0

  return av - bv
}

const scrollToTop = (ref: ComponentPublicInstance<unknown> | undefined): void => {
  if (window.scrollTo) {
    window.scrollTo(0, 0)
  }
  if (ref != null) {
    ref.$el.scrollTo(0, 0)
  }
}

/**
 * General typesafe compare function for objects.
 * Can be used to sort nullable objects by a field which is given with the keyToSort parameter
 *
 * @param keyToSort
 * @param direction
 * @param sortNullsToEnd
 */
const compare = <T>(
  keyToSort: keyof T,
  direction: 'asc' | 'desc' = 'asc',
  sortNullsToEnd = true
): ((a: T | undefined, b: T | undefined) => number) => {
  function compareNulls(a: unknown, b: unknown): number {
    if (!a && !b) return 0
    if (!a) return sortNullsToEnd ? 1 : -1
    return sortNullsToEnd ? -1 : 1
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function compareValues(valueA: any, valueB: any): number {
    if (valueA === valueB) return 0
    if (valueA > valueB) return direction === 'asc' ? 1 : -1
    return direction === 'asc' ? -1 : 1
  }

  return function (objectA: T | undefined, objectB: T | undefined): number {
    if (!objectA || !objectB) return compareNulls(objectA, objectB)

    const valueA = objectA[keyToSort]
    const valueB = objectB[keyToSort]

    if (!objectA || !objectB) return compareNulls(objectA, objectB)

    return compareValues(valueA, valueB)
  }
}

const isQuoteActivityUser = (user: unknown): user is QuoteActivityUser => {
  if ((user as QuoteActivityUser).fullName) {
    return true
  }
  return false
}

const getFullName = (
  user: ShortUser | User | OrderLimitUser | ContactUser | GuestUser | QuoteActivityUser
): string => {
  return isQuoteActivityUser(user) ? user.fullName : [user.firstName, user.lastName].join(' ')
}

const getFullNameAndCompany = (user: ShortUser | User): string => {
  return [user.firstName, user.lastName].join(' ') + ` (${user.companyName})`
}

const urltoFile = async (url: string, filename: string, mimeType: string): Promise<File> => {
  const res = await fetch(url, {
    headers: {
      'Content-Type': mimeType
    }
  })
  const buf = await res.arrayBuffer()
  return new File([buf], filename, { type: mimeType })
}

const saveAttachment = (attachment: Attachment): void => {
  let type: string
  switch (attachment.contentType) {
    case ContentType.XLS:
      type = 'application/vnd.ms-excel'
      break
    case ContentType.PDF:
      type = 'application/pdf'
      break
    default:
      type = 'application/octet-stream'
      break
  }

  urltoFile('data:' + type + ';base64,' + attachment.content, attachment.name, type).then(
    function (file) {
      saveAs(file)
    }
  )
}

const filterMenuItems = (menu: MenuItem[], portalSettings: PortalSettings): MenuItem[] => {
  return menu.filter((item) => {
    if (item.restriction) {
      if (portalSettings[item.restriction]) {
        return item
      }
    } else {
      return item
    }
  })
}

const parseFileName = (responseHeaders: RawAxiosResponseHeaders | AxiosResponseHeaders): string => {
  return responseHeaders['content-disposition'].split('filename="')[1].slice(0, -1)
}

const isGuest: ComputedRef<boolean> = computed(() =>
  toRef(reactive(router), 'currentRoute').value.path.startsWith('/guest')
)

const isEssentialPortal: ComputedRef<boolean> = computed(() => {
  const { branding } = useBranding()
  return branding.value?.portalType === PortalType.EssentialPortal
})

const isQuotesPortal: ComputedRef<boolean> = computed(() => {
  const { branding } = useBranding()
  return branding.value?.portalType === PortalType.QuotesPortal
})

const isQuotesPortalOrGuest: ComputedRef<boolean> = computed(() => {
  return isQuotesPortal.value || isGuest.value
})

const useTitle = (pretext: Ref<string>) => {
  const requestManager = useRequestManager().state
  const { t } = useI18n()
  const { branding } = useBranding()

  const title = computed(() => {
    if (requestManager.value.serverOffline || !branding.value) {
      return t('serverOffline.title')
    } else {
      return branding.value.portalName
        ? `${pretext.value} - ${branding.value.portalName}`
        : t('portal')
    }
  })

  vueUseTitle(title)
}

const isGeneratedShopDomain = computed(() => {
  return globalThis.location.hostname.endsWith('systemhaus.shop')
})

const isGeneratedQuotesDomain = computed(() => {
  return globalThis.location.hostname.endsWith('itquote.de')
})

export {
  compare,
  disableSort,
  emptyPlaceholderImage,
  filterMenuItems,
  fitCropImage,
  fitImage,
  getCurrencySymbol,
  getFullName,
  getFullNameAndCompany,
  getFullUrl,
  getSrcset,
  isEssentialPortal,
  isGeneratedQuotesDomain,
  isGeneratedShopDomain,
  isGuest,
  isQuoteActivityUser,
  isQuotesPortal,
  isQuotesPortalOrGuest,
  moneyCompare,
  numberCompare,
  parseFileName,
  placeholderImage,
  saveAttachment,
  scrollToTop,
  statusCompare,
  stringCompare,
  urltoFile,
  useTitle
}
